import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckoutForm } from '@/components/billing/checkout-form';
import { useCreateInvitations } from '@/hooks/invitation/use-invitation';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearProgress } from '@/utils/create-company-progress';
import { toast } from 'sonner';

interface OnboardingCheckoutProps {
  onComplete?: () => void;
  onCancel?: () => void;
  companyId?: string;
  planId?: string;
}

export function OnboardingCheckout({ onComplete, onCancel, companyId, planId: planIdProp }: OnboardingCheckoutProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const createInvitationsMutation = useCreateInvitations();
  const [hasSentInvitations, setHasSentInvitations] = useState(false);

  const planId = planIdProp || searchParams.get('planId');

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const pollForCompletion = async (companyId: string, maxAttempts = 10): Promise<boolean> => {
    const { companyOnboardingService } = await import('@/services/company-onboarding/company-onboarding.service');
    
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        const status = await companyOnboardingService.getStatus(companyId);
        if (status.isComplete) {
          return true;
        }
      } catch {
        // Continue polling
      }
    }
    return false;
  };

  const sendPendingInvitations = async () => {
    try {
      const pendingEmailsStr = localStorage.getItem('pendingInvitationEmails');
      if (!pendingEmailsStr) return;

      const pendingEmails: string[] = JSON.parse(pendingEmailsStr);
      if (pendingEmails.length === 0) {
        localStorage.removeItem('pendingInvitationEmails');
        return;
      }

      const validEmails = pendingEmails.filter((email) => {
        const trimmed = email.trim();
        return trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
      });

      if (validEmails.length === 0) {
        localStorage.removeItem('pendingInvitationEmails');
        return;
      }

      await createInvitationsMutation.mutateAsync({ emails: validEmails });
      localStorage.removeItem('pendingInvitationEmails');
      setHasSentInvitations(true);
    } catch {
      // Silent fail - invitations can be sent manually later
    }
  };

  const handleSuccess = async () => {
    // Only proceed if payment was actually successful
    // This is called by CheckoutForm when payment succeeds
    try {
      // Send pending invitations first
      if (!hasSentInvitations) {
        try {
          await sendPendingInvitations();
        } catch (inviteError) {
          // Invitations failure shouldn't block completion, but log it
          console.warn('Failed to send invitations:', inviteError);
        }
      }

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['onboardingStatus'] });
      await queryClient.invalidateQueries({ queryKey: ['subscription'] });
      await queryClient.invalidateQueries({ queryKey: ['companyOnboardingStatus'] });

      // Poll for completion to ensure backend has processed the payment
      // Only call onComplete after confirming completion
      if (companyId) {
        const isComplete = await pollForCompletion(companyId);
        if (isComplete) {
          // Only clear progress and call onComplete after confirmation
          clearProgress();
          localStorage.removeItem('pendingInvitationEmails');

          if (onComplete) {
            onComplete();
          }
        } else {
          // If polling didn't confirm completion, show warning but still proceed
          // The parent's handleStep3Complete will check status again
          toast.warning(t('onboarding.paymentProcessing') || 'Payment processed, verifying completion...');
          clearProgress();
          localStorage.removeItem('pendingInvitationEmails');
          
          // Call onComplete to let parent handle verification
          if (onComplete) {
            onComplete();
          }
        }
      } else {
        // No companyId means this is user onboarding, not company creation
        clearProgress();
        localStorage.removeItem('pendingInvitationEmails');
        
        if (onComplete) {
          onComplete();
        }
      }
    } catch (error) {
      // If anything fails in post-payment processing, show error
      const errorMessage = error instanceof Error ? error.message : t('onboarding.paymentProcessingError') || 'Error processing payment completion';
      toast.error(errorMessage);
      // Don't call onComplete if post-payment processing fails
      // Stay on checkout page so user can see the error
    }
  };

  if (!planId) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">{t('onboarding.paymentSubtitle') || 'Please select a plan first'}</p>
      </div>
    );
  }

  return (
    <CheckoutForm
      planId={planId}
      onSuccess={handleSuccess}
      onCancel={handleCancel}
      suppressPaymentSuccessToast={true}
    />
  );
}
